import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AppContext } from "../contexts/AppContext";
import FilterPanel from "../components/FilterPanel";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Header } from "../layout/Header";
import { Text } from "../components/catalyst/text";
import { Filter, RefreshCcw } from "lucide-react";
import { createClustersWithRetry, createClustersWithRetryOptimized, createClustersUniversal } from "../services/clusteringService";

function GeneratePage() {
  const {
    API_BASE_URL,
    setError,
    setLoading,
    setData,
    filterOptions,
    setFilterOptions,
    loading,
    error,
  } = useContext(AppContext);
  const navigate = useNavigate();
  
  // Get auth token from localStorage
  const token = localStorage.getItem('isco_jwt');
  
  // Stopwatch state for timing schedule generation
  const [elapsedTime, setElapsedTime] = useState(0);
  const intervalRef = useRef(null);
  
  // Loading state for each step
  const [loadingState, setLoadingState] = useState({
    step: '',
    message: ''
  });

  const fetchFilterOptions = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.regions?.length > 0) {
        filters.regions.forEach(region => params.append('regions', region));
      }
      
      const response = await axios.get(`${API_BASE_URL}/filter_options`, { params });
      
      if (response.data && typeof response.data === "object") {
        setFilterOptions(prevOptions => ({
          ...prevOptions,
          ...response.data,
        }));
      } else {
        setFilterOptions(prevOptions => prevOptions || {});
      }
    } catch (err) {
      setError("Could not load filter options. Filtering might be limited.");
      console.error("Error fetching filter options:", err);
    }
  };

  // Set initial state with default clustering parameters
  const [selectedFilters, setSelectedFilters] = useState({
    regions: [],
    states: [],
    markets: [],
    priority_buckets: [],
    customer_priorities: [],
    center: null,
    radius: 0,
    clustering_params: {
      eps: 0.0019, // New Default Value (deprecated)
      min_samples: 12, // Deprecated
      use_v3: true, // Use new geographic clustering algorithm
      stops_per_day: 5, // Max stops per day
    },
  });

  useEffect(() => {
    if (!filterOptions || Object.keys(filterOptions).length === 0) {
      fetchFilterOptions();
    }
  }, [API_BASE_URL, filterOptions, setFilterOptions, setError]);

  // Stopwatch effect - start/stop timer based on loading state
  useEffect(() => {
    if (loading) {
      // Start the stopwatch
      setElapsedTime(0);
      intervalRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      // Stop the stopwatch
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loading]);

  // Format elapsed time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch data function (now triggers navigation)
  const fetchDataAndNavigate = async () => {
    if (!token) {
      setError("Authentication required. Please log in.");
      return;
    }
    
    setLoading(true);
    setError("");
    setData([]); // Clear previous data
    setLoadingState({ step: '', message: '' });
    
    try {
      // Build filters object
      const filters = {};
      if (selectedFilters.regions?.length > 0)
        filters.regions = selectedFilters.regions;
      if (selectedFilters.markets?.length > 0)
        filters.markets = selectedFilters.markets;
      if (selectedFilters.states?.length > 0)
        filters.states = selectedFilters.states;
      if (selectedFilters.priority_buckets?.length > 0)
        filters.priority_buckets = selectedFilters.priority_buckets;
      if (selectedFilters.customer_priorities?.length > 0)
        filters.customer_priorities = selectedFilters.customer_priorities;
      if (selectedFilters.center && selectedFilters.radius > 0) {
        filters.center = selectedFilters.center;
        filters.radius = selectedFilters.radius;
      }
      if (selectedFilters.startDate) {
        filters.startDate = selectedFilters.startDate;
      }
      if (selectedFilters.endDate) {
        filters.endDate = selectedFilters.endDate;
      }
      
      const clusteringParams = selectedFilters.clustering_params || {
        eps: 0.0019,
        min_samples: 12,
      };
      
      // Use the universal clustering service that automatically selects v2 or v1 API
      const schedulesData = await createClustersUniversal(
        filters,
        clusteringParams,
        token,
        (state) => setLoadingState(state) // Progress callback
      );
      
      if (schedulesData && schedulesData.length > 0) {
        // The response from save-itinerary already has the correct format
        console.log("Total processed schedules:", schedulesData.length);
        console.log("Processed data before setData:", schedulesData);
        setData(schedulesData); // Store processed data in context
        navigate("/schedules");
      } else {
        setError("No schedules generated. Try adjusting filters or clustering parameters.");
      }
      
    } catch (err) {
      console.error("Error in clustering process:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch data. Please try again later.";
      setError(errorMessage);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handler for applying filters and generating schedules
  const handleApplyFilters = () => {
    fetchDataAndNavigate(); // Call the function that fetches and navigates
  };

  // Handler for filter changes from the FilterPanel
  const handleFilterChange = (newFilters) => {
    // Detect if regions have changed
    const regionsChanged = JSON.stringify(newFilters.regions) !== JSON.stringify(selectedFilters.regions);
    
    setSelectedFilters(newFilters);

    // If regions changed, refetch the filter options
    if (regionsChanged) {
      fetchFilterOptions({ regions: newFilters.regions });
    }
  };

  return (
    <div>
      <Header />
      <div className="grid grid-cols-1 gap-6">
        <Card className="overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between p-4">
            <CardTitle className="text-lg font-semibold">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filter Opportunities & Generate Schedules
              </div>
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                const today = new Date();
                const endDate = new Date(today.getTime() + (7 * 24 * 60 * 60 * 1000));
                const resetFilters = {
                  regions: [],
                  states: [],
                  markets: [],
                  priority_buckets: [],
                  customer_priorities: [],
                  startDate: today.toISOString().split('T')[0],
                  endDate: endDate.toISOString().split('T')[0],
                  center: null,
                  radius: 0,
                  clustering_params: { eps: 0.0019, min_samples: 12 },
                };
                setSelectedFilters(resetFilters);
              }}
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> Reset Filters
            </Button>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <FilterPanel
              filterOptions={filterOptions || {}}
              selectedFilters={selectedFilters}
              onChange={handleFilterChange}
              onApply={handleApplyFilters}
              baseUrl={API_BASE_URL}
              token={token}
            />
            
            {error && (
              <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-md">
                {error}
              </div>
            )}
            {loading && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full shadow-xl">
                  <div className="flex flex-col items-center space-y-6">
                    <svg
                      className="animate-spin h-12 w-12 text-primary"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    
                    <h2 className="text-xl font-semibold text-center">
                      {loadingState.step ? loadingState.message : 'AI is generating your schedules'}
                    </h2>
                    <p className="text-center text-gray-600 dark:text-gray-300">
                      This may take up to a few minutes
                    </p>
                    
                    {/* Stopwatch Display */}
                    <div className="text-center">
                      <div className="text-3xl font-mono font-bold text-primary mb-2">
                        {formatTime(elapsedTime)}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Elapsed time
                      </p>
                    </div>
                    
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div className="bg-primary h-2.5 rounded-full animate-pulse w-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default GeneratePage;
